import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import VoteFormModal from '../../components/admin/VoteFormModal';
import { apiRequest } from '../../utils/api';

const VotesPage = () => {
    const { organizationId } = useParams();
    const [votes, setVotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingVote, setEditingVote] = useState(null);
    const [confirmAction, setConfirmAction] = useState(null);
    const [, setTick] = useState(0); // Force re-render for time updates
    
    // Extend/Update campaign modal state
    const [showExtendModal, setShowExtendModal] = useState(false);
    const [extendingVote, setExtendingVote] = useState(null);
    const [extendDate, setExtendDate] = useState('');
    const [extendTime, setExtendTime] = useState('');
    const [extendError, setExtendError] = useState('');

    useEffect(() => {
        loadVotes();
    }, [organizationId]);

    // Auto-refresh every 10 seconds for active votes
    useEffect(() => {
        const hasActiveVote = votes.some(v => v.status === 'voting' || v.status === 'campaign_active');
        if (hasActiveVote) {
            const interval = setInterval(() => {
                loadVotes();
            }, 10000); // Refresh every 10 seconds
            return () => clearInterval(interval);
        }
    }, [votes]);

    // Update remaining time display every second
    useEffect(() => {
        const timer = setInterval(() => {
            setTick(t => t + 1);
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    const loadVotes = async () => {
        setLoading(true);
        try {
            const response = await apiRequest(`/organizations/${organizationId}/votes`);
            setVotes(response.data || []);
        } catch (error) {
            console.error('Load votes error:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await apiRequest(`/organizations/${organizationId}/votes/${id}`, {
                method: 'DELETE'
            });
            loadVotes();
            setConfirmAction(null);
        } catch (error) {
            alert('Oylama silinirken bir hata oluştu');
        }
    };

    const handleStartVoting = async (id) => {
        try {
            await apiRequest(`/organizations/${organizationId}/votes/${id}/start`, {
                method: 'POST'
            });
            loadVotes();
            setConfirmAction(null);
        } catch (error) {
            alert(error.message || 'Oylama başlatılırken bir hata oluştu');
        }
    };

    const handleEndVoting = async (id) => {
        try {
            await apiRequest(`/organizations/${organizationId}/votes/${id}/end`, {
                method: 'POST'
            });
            loadVotes();
            setConfirmAction(null);
        } catch (error) {
            alert(error.message || 'Oylama sonlandırılırken bir hata oluştu');
        }
    };

    const handleCloseVote = async (id) => {
        try {
            await apiRequest(`/organizations/${organizationId}/votes/${id}/close`, {
                method: 'POST'
            });
            loadVotes();
            setConfirmAction(null);
        } catch (error) {
            alert(error.message || 'Kampanya kapatılırken bir hata oluştu');
        }
    };

    // Open extend/update modal
    const openExtendModal = (vote) => {
        setExtendingVote(vote);
        setExtendError('');
        
        // Use existing campaign end date if available, otherwise 1 hour from now
        let defaultEnd;
        if (vote.campaign_end_date) {
            defaultEnd = new Date(vote.campaign_end_date);
            // If the date is in the past, set to 1 hour from now
            if (defaultEnd <= new Date()) {
                defaultEnd = new Date(Date.now() + 60 * 60 * 1000);
            }
        } else {
            defaultEnd = new Date(Date.now() + 60 * 60 * 1000);
        }
        
        // Format date as YYYY-MM-DD using local time
        const year = defaultEnd.getFullYear();
        const month = String(defaultEnd.getMonth() + 1).padStart(2, '0');
        const day = String(defaultEnd.getDate()).padStart(2, '0');
        const hours = String(defaultEnd.getHours()).padStart(2, '0');
        const minutes = String(defaultEnd.getMinutes()).padStart(2, '0');
        
        setExtendDate(`${year}-${month}-${day}`);
        setExtendTime(`${hours}:${minutes}`);
        setShowExtendModal(true);
    };

    // Validate extend date/time
    const validateExtendDateTime = () => {
        if (!extendDate || !extendTime) {
            setExtendError('Lütfen tarih ve saat girin');
            return false;
        }
        const newEndDate = new Date(`${extendDate}T${extendTime}:00`);
        if (newEndDate <= new Date()) {
            setExtendError('Bitiş tarihi ve saati gelecekte olmalıdır');
            return false;
        }
        setExtendError('');
        return true;
    };

    // Handle extend/update campaign
    const handleExtendCampaign = async () => {
        if (!validateExtendDateTime()) {
            return;
        }

        const newEndDate = new Date(`${extendDate}T${extendTime}:00`);

        try {
            const endpoint = extendingVote.status === 'completed' 
                ? `/organizations/${organizationId}/votes/${extendingVote.id}/extend`
                : `/organizations/${organizationId}/votes/${extendingVote.id}/update-end-date`;
            
            await apiRequest(endpoint, {
                method: 'POST',
                body: JSON.stringify({
                    campaign_end_date: newEndDate.toISOString()
                })
            });
            
            loadVotes();
            setShowExtendModal(false);
            setExtendingVote(null);
            setExtendDate('');
            setExtendTime('');
            setExtendError('');
        } catch (error) {
            setExtendError(error.message || 'Kampanya güncellenirken bir hata oluştu');
        }
    };

    // Check if voting time has expired
    const isVotingExpired = (vote) => {
        if (vote.status !== 'voting') return false;
        if (!vote.voting_end_date) return false;
        return new Date(vote.voting_end_date) < new Date();
    };

    // Get real-time phase
    const getRealPhase = (vote) => {
        if (vote.phase) return vote.phase;
        if (isVotingExpired(vote)) return 'expired';
        return vote.status;
    };

    const getStatusLabel = (vote) => {
        const phase = getRealPhase(vote);
        const statusLabels = {
            draft: 'Taslak',
            pending: 'Beklemede',
            active: 'Hazır',
            voting: '🗳️ Oylama Devam Ediyor',
            expired: '⏰ Süre Doldu - Sonuçlandırılmayı Bekliyor',
            calculating: 'Hesaplanıyor...',
            campaign_active: '🎉 Kampanya Aktif',
            completed: 'Tamamlandı',
            archived: 'Arşivlendi'
        };
        return statusLabels[phase] || vote.status;
    };

    const getStatusColor = (vote) => {
        const phase = getRealPhase(vote);
        const statusColors = {
            draft: 'bg-gray-100 text-gray-800',
            pending: 'bg-yellow-100 text-yellow-800',
            active: 'bg-blue-100 text-blue-800',
            voting: 'bg-purple-100 text-purple-800 animate-pulse',
            expired: 'bg-orange-100 text-orange-800',
            calculating: 'bg-orange-100 text-orange-800',
            campaign_active: 'bg-green-100 text-green-800',
            completed: 'bg-gray-100 text-gray-600',
            archived: 'bg-red-100 text-red-800'
        };
        return statusColors[phase] || 'bg-gray-100 text-gray-800';
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleString('tr-TR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const formatDuration = (minutes) => {
        if (minutes < 60) return `${minutes} dakika`;
        if (minutes < 1440) return `${Math.floor(minutes / 60)} saat`;
        return `${Math.floor(minutes / 1440)} gün`;
    };

    // Calculate remaining time dynamically from voting_end_date
    const getRemainingTime = useCallback((vote) => {
        let endDate = null;
        
        if (vote.status === 'voting' && vote.voting_end_date) {
            endDate = new Date(vote.voting_end_date);
        } else if (vote.status === 'campaign_active' && vote.campaign_end_date) {
            endDate = new Date(vote.campaign_end_date);
        }
        
        if (!endDate) return null;
        
        const now = new Date();
        const ms = endDate - now;
        
        if (ms <= 0) return null;
        
        const hours = Math.floor(ms / (1000 * 60 * 60));
        const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((ms % (1000 * 60)) / 1000);

        if (hours > 0) return `${hours}s ${minutes}dk ${seconds}sn`;
        if (minutes > 0) return `${minutes}dk ${seconds}sn`;
        return `${seconds}sn`;
    }, []);

    return (
        <AdminLayout>
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 -m-6 p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                            🗳️ Oylamalar
                        </h1>
                        <p className="text-gray-500 mt-1">Ürün oylamaları ve kampanyaları yönetin</p>
                    </div>
                    <button
                        onClick={() => {
                            setEditingVote(null);
                            setShowModal(true);
                        }}
                        className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg hover:shadow-xl flex items-center gap-2 font-medium"
                    >
                        <span className="text-xl">+</span>
                        Yeni Oylama Oluştur
                    </button>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <div className="animate-spin rounded-full h-16 w-16 border-4 border-purple-200 border-t-purple-600"></div>
                    </div>
                ) : votes.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl shadow-lg border border-gray-100">
                        <div className="text-6xl mb-4">🗳️</div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Henüz oylama yok</h3>
                        <p className="text-gray-500 mb-6">İlk oylamanızı oluşturarak müşterilerinizin favori ürünlerini keşfedin</p>
                        <button
                            onClick={() => setShowModal(true)}
                            className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg"
                        >
                            İlk Oylamayı Oluştur
                        </button>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {votes.map((vote) => (
                            <div 
                                key={vote.id} 
                                className={`bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transition-all hover:shadow-xl ${
                                    vote.status === 'voting' ? 'ring-2 ring-purple-400 ring-offset-2' : ''
                                }`}
                            >
                                {/* Vote Header */}
                                <div className="p-6 border-b border-gray-100">
                                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                                        <div className="flex-1">
                                            <div className="flex items-center gap-3 mb-2">
                                                <h3 className="text-xl font-bold text-gray-800">{vote.title}</h3>
                                                <span className={`px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(vote)}`}>
                                                    {getStatusLabel(vote)}
                                                </span>
                                            </div>
                                            {vote.description && (
                                                <p className="text-gray-500 text-sm">{vote.description}</p>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {/* Remaining Time Badge - Dynamic */}
                                            {vote.status === 'voting' && getRemainingTime(vote) && (
                                                <div className="bg-purple-100 text-purple-700 px-4 py-2 rounded-lg font-mono text-sm animate-pulse">
                                                    ⏱️ {getRemainingTime(vote)}
                                                </div>
                                            )}
                                            {vote.status === 'voting' && !getRemainingTime(vote) && (
                                                <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-lg font-mono text-sm">
                                                    ⏰ Süre Doldu!
                                                </div>
                                            )}
                                            {vote.status === 'campaign_active' && getRemainingTime(vote) && (
                                                <div className="bg-green-100 text-green-700 px-4 py-2 rounded-lg font-mono text-sm">
                                                    🎉 Kampanya: {getRemainingTime(vote)}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Vote Stats */}
                                <div className="p-6 bg-gradient-to-r from-slate-50 to-white">
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                            <div className="text-2xl font-bold text-purple-600">{vote.options?.length || 0}</div>
                                            <div className="text-xs text-gray-500">Ürün</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                            <div className="text-2xl font-bold text-blue-600">{vote.total_votes || 0}</div>
                                            <div className="text-xs text-gray-500">Toplam Oy</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                            <div className="text-2xl font-bold text-amber-600">{formatDuration(vote.voting_duration_minutes || 60)}</div>
                                            <div className="text-xs text-gray-500">Oylama Süresi</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                                            <div className="text-2xl font-bold text-green-600 mb-1">
                                                {vote.winner ? '🏆' : '-'}
                                            </div>
                                            <div className="text-xs text-gray-500">Kazanan</div>
                                            {vote.winner && (
                                                <div className="text-xs font-medium text-amber-600 mt-1 truncate max-w-[100px]" title={vote.winner.product_name}>
                                                    {vote.winner.product_name}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Products with Vote Distribution Chart */}
                                    {vote.options && vote.options.length > 0 && (
                                        <div className="mb-6">
                                            <h4 className="text-sm font-medium text-gray-700 mb-3">Oy Dağılımı</h4>
                                            <div className="space-y-3">
                                                {/* Sort by vote count descending */}
                                                {[...vote.options]
                                                    .sort((a, b) => (b.vote_count || 0) - (a.vote_count || 0))
                                                    .map((option, idx) => {
                                                        const isWinner = vote.winner?.product_id === option.product_id;
                                                        const votePercent = option.vote_percentage || 0;
                                                        const voteCount = option.vote_count || 0;
                                                        
                                                        return (
                                                            <div 
                                                                key={option.id || idx}
                                                                className={`relative bg-white rounded-xl p-4 shadow-sm border ${
                                                                    isWinner 
                                                                        ? 'border-amber-400 ring-2 ring-amber-200' 
                                                                        : 'border-gray-200'
                                                                }`}
                                                            >
                                                                {/* Progress Bar Background */}
                                                                <div 
                                                                    className={`absolute inset-0 rounded-xl transition-all duration-500 ${
                                                                        isWinner 
                                                                            ? 'bg-gradient-to-r from-amber-100/50 to-yellow-100/50' 
                                                                            : 'bg-gradient-to-r from-purple-100/30 to-pink-100/30'
                                                                    }`}
                                                                    style={{ width: `${votePercent}%` }}
                                                                />
                                                                
                                                                {/* Content */}
                                                                <div className="relative flex items-center gap-4">
                                                                    {/* Rank Badge */}
                                                                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                                                                        idx === 0 && vote.total_votes > 0
                                                                            ? 'bg-amber-500 text-white' 
                                                                            : 'bg-gray-200 text-gray-600'
                                                                    }`}>
                                                                        {idx === 0 && vote.total_votes > 0 ? '🏆' : idx + 1}
                                                                    </div>
                                                                    
                                                                    {/* Product Image */}
                                                                    {option.product_image ? (
                                                                        <img 
                                                                            src={option.product_image.startsWith('http') ? option.product_image : option.product_image}
                                                                            alt={option.product_name}
                                                                            className="w-12 h-12 rounded-lg object-cover"
                                                                        />
                                                                    ) : (
                                                                        <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center text-xl">
                                                                            🍽️
                                                                        </div>
                                                                    )}
                                                                    
                                                                    {/* Product Info */}
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="flex items-center gap-2">
                                                                            <span className="font-semibold text-gray-800 truncate">
                                                                                {option.product_name}
                                                                            </span>
                                                                            {option.discount_percentage && (
                                                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                                                                    %{option.discount_percentage}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        
                                                                        {/* Progress Bar */}
                                                                        <div className="mt-2 w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                                                                            <div 
                                                                                className={`h-full rounded-full transition-all duration-1000 ${
                                                                                    isWinner 
                                                                                        ? 'bg-gradient-to-r from-amber-400 to-yellow-500' 
                                                                                        : 'bg-gradient-to-r from-purple-500 to-pink-500'
                                                                                }`}
                                                                                style={{ width: `${votePercent}%` }}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    
                                                                    {/* Vote Stats */}
                                                                    <div className="text-right">
                                                                        <div className={`text-2xl font-bold ${
                                                                            isWinner ? 'text-amber-600' : 'text-purple-600'
                                                                        }`}>
                                                                            {votePercent}%
                                                                        </div>
                                                                        <div className="text-sm text-gray-500">
                                                                            {voteCount} oy
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                            </div>
                                        </div>
                                    )}

                                    {/* Campaign Info */}
                                    {vote.campaign_end_date && (
                                        <div className="text-xs text-gray-500 mb-4">
                                            <span className="font-medium">Kampanya Bitiş:</span> {formatDate(vote.campaign_end_date)}
                                        </div>
                                    )}

                                    {/* Actions */}
                                    <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-100">
                                        {/* Draft/Pending - Can edit and start */}
                                        {['draft', 'pending', 'active'].includes(vote.status) && (
                                            <>
                                                <button
                                                    onClick={() => {
                                                        setEditingVote(vote);
                                                        setShowModal(true);
                                                    }}
                                                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm font-medium"
                                                >
                                                    ✏️ Düzenle
                                                </button>
                                                <button
                                                    onClick={() => setConfirmAction({ type: 'start', vote })}
                                                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition text-sm font-medium shadow-lg"
                                                >
                                                    🚀 Oylamayı Başlat
                                                </button>
                                            </>
                                        )}

                                        {/* Voting - Can end voting */}
                                        {vote.status === 'voting' && (
                                            <button
                                                onClick={() => setConfirmAction({ type: 'end', vote })}
                                                className={`px-4 py-2 rounded-lg transition text-sm font-medium ${
                                                    isVotingExpired(vote)
                                                        ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600 shadow-lg animate-pulse'
                                                        : 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                                }`}
                                            >
                                                {isVotingExpired(vote) ? '🏁 Sonuçları Hesapla' : '⏹️ Oylamayı Bitir'}
                                            </button>
                                        )}

                                        {/* Campaign Active - Can close campaign */}
                                        {vote.status === 'campaign_active' && (
                                            <>
                                                <button
                                                    onClick={() => openExtendModal(vote)}
                                                    className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition text-sm font-medium"
                                                >
                                                    ⏰ Süre Güncelle
                                                </button>
                                                <button
                                                    onClick={() => setConfirmAction({ type: 'close', vote })}
                                                    className="px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition text-sm font-medium"
                                                >
                                                    ❌ Kampanyayı Bitir
                                                </button>
                                            </>
                                        )}

                                        {/* Restart completed campaign */}
                                        {vote.status === 'completed' && vote.winner_product_id && (
                                            <button
                                                onClick={() => openExtendModal(vote)}
                                                className="px-4 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition text-sm font-medium"
                                            >
                                                🔄 Devam Ettir
                                            </button>
                                        )}

                                        {/* Delete for non-active votes */}
                                        {!['voting', 'campaign_active'].includes(vote.status) && (
                                            <button
                                                onClick={() => setConfirmAction({ type: 'delete', vote })}
                                                className="px-4 py-2 bg-red-50 text-red-600 rounded-lg hover:bg-red-100 transition text-sm font-medium"
                                            >
                                                🗑️ Sil
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Vote Form Modal */}
                <VoteFormModal
                    isOpen={showModal}
                    onClose={() => {
                        setShowModal(false);
                        setEditingVote(null);
                    }}
                    vote={editingVote}
                    organizationId={organizationId}
                    onSuccess={loadVotes}
                />

                {/* Confirmation Modal */}
                {confirmAction && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 transform transition-all">
                            <div className="text-center mb-6">
                                {confirmAction.type === 'start' && (
                                    <>
                                        <div className="text-5xl mb-4">🚀</div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">Oylamayı Başlat</h3>
                                        <p className="text-gray-500">
                                            "{confirmAction.vote.title}" oylamasını başlatmak istediğinize emin misiniz?
                                            Oylama {formatDuration(confirmAction.vote.voting_duration_minutes || 60)} sürecek.
                                        </p>
                                    </>
                                )}
                                {confirmAction.type === 'end' && (
                                    <>
                                        <div className="text-5xl mb-4">⏹️</div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">Oylamayı Bitir</h3>
                                        <p className="text-gray-500">
                                            Oylamayı şimdi bitirmek ve kampanyayı başlatmak istediğinize emin misiniz?
                                            En çok oy alan ürün kazanan olarak belirlenecek.
                                        </p>
                                    </>
                                )}
                                {confirmAction.type === 'close' && (
                                    <>
                                        <div className="text-5xl mb-4">❌</div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">Kampanyayı Bitir</h3>
                                        <p className="text-gray-500">
                                            Kampanyayı şimdi bitirmek istediğinize emin misiniz?
                                            Kazanan ürün normal fiyatına dönecek.
                                        </p>
                                    </>
                                )}
                                {confirmAction.type === 'delete' && (
                                    <>
                                        <div className="text-5xl mb-4">🗑️</div>
                                        <h3 className="text-xl font-bold text-gray-800 mb-2">Oylamayı Sil</h3>
                                        <p className="text-gray-500">
                                            Bu oylamayı silmek istediğinize emin misiniz?
                                            Bu işlem geri alınamaz.
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="flex gap-3">
                                <button
                                    onClick={() => setConfirmAction(null)}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={() => {
                                        if (confirmAction.type === 'start') handleStartVoting(confirmAction.vote.id);
                                        else if (confirmAction.type === 'end') handleEndVoting(confirmAction.vote.id);
                                        else if (confirmAction.type === 'close') handleCloseVote(confirmAction.vote.id);
                                        else if (confirmAction.type === 'delete') handleDelete(confirmAction.vote.id);
                                    }}
                                    className={`flex-1 px-4 py-3 rounded-xl transition font-medium ${
                                        confirmAction.type === 'delete' 
                                            ? 'bg-red-600 text-white hover:bg-red-700'
                                            : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white hover:from-purple-700 hover:to-pink-700'
                                    }`}
                                >
                                    {confirmAction.type === 'start' && 'Başlat'}
                                    {confirmAction.type === 'end' && 'Bitir'}
                                    {confirmAction.type === 'close' && 'Kapat'}
                                    {confirmAction.type === 'delete' && 'Sil'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Extend/Update Campaign Modal */}
                {showExtendModal && extendingVote && (
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                            <div className="text-center mb-6">
                                <div className="text-5xl mb-4">
                                    {extendingVote.status === 'completed' ? '🔄' : '⏰'}
                                </div>
                                <h3 className="text-xl font-bold text-gray-800 mb-2">
                                    {extendingVote.status === 'completed' 
                                        ? 'Kampanyayı Devam Ettir' 
                                        : 'Kampanya Süresini Güncelle'}
                                </h3>
                                <p className="text-gray-500 text-sm">
                                    {extendingVote.status === 'completed'
                                        ? `"${extendingVote.title}" kampanyasını yeniden başlatmak için bitiş tarihini girin.`
                                        : `"${extendingVote.title}" kampanyasının bitiş tarihini güncelleyin.`}
                                </p>
                            </div>

                            <div className="space-y-4 mb-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Bitiş Tarihi
                                    </label>
                                    <input
                                        type="date"
                                        value={extendDate}
                                        onChange={(e) => {
                                            setExtendDate(e.target.value);
                                            setExtendError('');
                                        }}
                                        min={new Date().toISOString().split('T')[0]}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        Bitiş Saati
                                    </label>
                                    <input
                                        type="time"
                                        value={extendTime}
                                        onChange={(e) => {
                                            setExtendTime(e.target.value);
                                            setExtendError('');
                                        }}
                                        className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                                    />
                                    <p className="text-xs text-gray-400 mt-1">
                                        Şu anki saat: {new Date().toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                
                                {/* Error message */}
                                {extendError && (
                                    <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
                                        <p className="text-red-600 text-sm font-medium flex items-center gap-2">
                                            <span>⚠️</span> {extendError}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3">
                                <button
                                    onClick={() => {
                                        setShowExtendModal(false);
                                        setExtendingVote(null);
                                        setExtendDate('');
                                        setExtendTime('');
                                        setExtendError('');
                                    }}
                                    className="flex-1 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition font-medium"
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleExtendCampaign}
                                    className="flex-1 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl hover:from-purple-700 hover:to-pink-700 transition font-medium"
                                >
                                    {extendingVote.status === 'completed' ? 'Devam Ettir' : 'Güncelle'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
};

export default VotesPage;
